import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Loader2, ShoppingBag, Store, AlertCircle, Home, LogIn } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";

/**
 * RedeemPage - Landing page for scanned QR codes
 * 
 * URL format: /redeem/:code
 * 
 * Behavior:
 * - If merchant is logged in: auto-validate and show redemption UI
 * - If not logged in: prompt to log in as merchant
 * - If sender/admin: show message that only merchants can redeem
 */
export default function RedeemPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  
  const [pageState, setPageState] = useState("loading"); // loading, login_required, wrong_role, ready

  useEffect(() => {
    if (authLoading) return;
    
    if (!user) {
      setPageState("login_required");
      return;
    }
    
    if (user.role === "merchant") {
      // Merchant logged in - redirect to dashboard with the code pre-filled
      // Store code in sessionStorage so dashboard can pick it up
      sessionStorage.setItem("pendingRedeemCode", code);
      navigate("/app/merchant?redeem=true");
      return;
    }
    
    // User is logged in but not a merchant
    setPageState("wrong_role");
  }, [user, authLoading, code, navigate]);

  // Loading
  if (pageState === "loading" || authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-emerald-800 to-blue-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
            <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Processing...</h1>
          <p className="text-slate-600">Verifying redemption code.</p>
        </div>
      </div>
    );
  }

  // Login required
  if (pageState === "login_required") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-emerald-800 to-blue-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
            <ShoppingBag className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Redemption Code</h1>
          <p className="text-slate-600 mb-6">
            Log in as a merchant (Colmado) to redeem this purchase authorization.
          </p>
          
          <div className="bg-slate-50 rounded-xl p-4 mb-6">
            <p className="text-sm text-slate-500 mb-1">Code</p>
            <p className="font-mono text-lg font-bold text-slate-900">{code}</p>
          </div>

          <div className="space-y-3">
            <Link to={`/login?redirect=/redeem/${code}&role=merchant`}>
              <Button className="w-full bg-emerald-600 hover:bg-emerald-700 h-12">
                <LogIn className="w-4 h-4 mr-2" />
                Log in as Colmado
              </Button>
            </Link>
            <Link to="/">
              <Button variant="outline" className="w-full">
                <Home className="w-4 h-4 mr-2" />
                Go Home
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Wrong role (not a merchant)
  if (pageState === "wrong_role") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-emerald-800 to-blue-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-8 h-8 text-amber-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Merchant Access Only</h1>
          <p className="text-slate-600 mb-6">
            Only registered Colmados (merchants) can redeem purchase authorizations.
          </p>
          
          <div className="bg-slate-50 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-center gap-2 text-slate-600">
              <Store className="w-5 h-5" />
              <span>You are logged in as: <strong>{user?.role}</strong></span>
            </div>
          </div>

          <div className="space-y-3">
            <Link to="/">
              <Button variant="outline" className="w-full">
                <Home className="w-4 h-4 mr-2" />
                Go Home
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
